import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, BarChart2, TrendingUp, DollarSign, Briefcase, Loader } from 'lucide-react';
import axiosInstance from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import './Investor.css';

const Investor = () => {
  const [userData, setUserData] = useState({
    totalAUM: 0,
    Year_to_date_returns: 0,
    fundName: '',
    totalReturn: 0,
    recentActivities: [],
    yearsInFund: 0,
    lastUpdated: null,
    totalReturn$:0
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        logout();
        navigate('/login');
        throw new Error('No authentication token found');
      }

      const response = await axiosInstance.get('/api/users/investor');
      
      if (response.data) {
        // Handle both possible response structures
        const investorData = response.data.data || response.data;
        
        setUserData({
          totalAUM: investorData.totalAUM || 0,
          Year_to_date_returns: investorData.Year_to_date_returns || 0,
          fundName: investorData.fundName || 'Not Assigned',
          totalReturn: investorData.totalReturn || 0,
          recentActivities: investorData.recentActivities || [],
          yearsInFund: investorData.yearsInFund || 0,
          totalReturn$: investorData.totalReturn$ || 0,
          lastUpdated: investorData.lastUpdated || new Date().toISOString()
        });
        
        setError(null);
      } else {
        throw new Error('Invalid data received from server');
      }
    } catch (error) {
      console.error('Error fetching investor data:', error);
      
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
        return;
      }

      setError(error.message || 'Failed to load investor data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
    
    // Refresh data every 5 minutes
    const interval = setInterval(fetchUserData, 300000);
    return () => clearInterval(interval);
  }, );

  const handleRefresh = () => {
    setLoading(true);
    fetchUserData();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercentage = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value / 100);
  };
  const { user } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <Loader className="spinner" size={40} />
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error Loading Dashboard</h2>
        <p>{error}</p>
        <button onClick={handleRefresh} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="investor-container">
      <div className="investor-header">
      <Link to="/profile" className="profile-link">
            <User size={20} />
            <span>Welcome, {user?.name || 'Investor'} </span>
          </Link>
        <div className="header-actions">
          <button 
            onClick={handleRefresh} 
            className="refresh-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader className="spinner" size={20} />
                <span>Refreshing...</span>
              </>
            ) : (
              <>
                <TrendingUp size={20} />
                <span>Refresh</span>
              </>
            )}
          </button>
          <Link to="/payment" className="deposit-button">
            <DollarSign size={20} />
            Make a Deposit 
        </Link>
        </div>
      </div>

      <div className="metric-grid">
        <div className="metric-card">
          <DollarSign size={40} />
          <h3>{formatCurrency(userData.totalAUM)}</h3>
          <p>Investment Amount</p>
        </div>
        <div className="metric-card">
          <TrendingUp size={40} />
          <h3>{formatPercentage(userData.Year_to_date_returns)}</h3>
          <p>YTD Returns</p>
        </div>
        <div className="metric-card">
          <Briefcase size={40} />
          <h3>{userData.yearsInFund}</h3>
          <p>Years In Fund</p>
        </div>
        <div className="metric-card">
          <BarChart2 size={40} />
          <h3>{formatPercentage(userData.totalReturn)}</h3>
          <p>Total Return</p>
        </div>
      </div>

      <div className="fund-details">
        <div className="metric-card">
          <Briefcase size={40} />
          <h3>{userData.fundName || 'Not Assigned'}</h3>
          <p>Current Fund</p>
        </div>
      </div>

      <section className="recent-activities">
        <div className="section-header">
          <h2>Recent Activities</h2>
          <span className="activity-count">
            {userData.recentActivities?.length || 0} activities
          </span>
        </div>
        <div className="activity-list">
          {Array.isArray(userData.recentActivities) && userData.recentActivities.length > 0 ? (
            userData.recentActivities.map((activity, index) => (
              <div className="activity-item" key={index}>
                <div className="activity-content">
                  <span className="activity-text">{activity}</span>
                  <span className="activity-date">
                    {new Date().toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="no-activities">
              <p>No recent activities to display</p>
              <small>New activities will appear here as they occur</small>
            </div>
          )}
        </div>
      </section>

      {userData.lastUpdated && (
        <div className="last-updated">
          Last updated: {new Date(userData.lastUpdated).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default Investor;